import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Calendar, 
  Clock, 
  ArrowRight, 
  User,
  Tag,
  TrendingUp,
  X
} from 'lucide-react';

// Blog Posts Data
const blogPosts = [
  {
    id: 1,
    title: 'Why Background Verification Is Critical for Growing Companies',
    excerpt: 'As companies scale, the risk of hiring mistakes grows exponentially. Learn why implementing robust background verification processes is essential for sustainable growth and how it protects your organization.',
    content: `As companies scale, the risk of hiring mistakes grows exponentially. A single bad hire can cost organizations anywhere from 30% to 150% of the employee's annual salary when considering recruitment costs, training expenses, lost productivity, and potential damage to team morale.

Background verification serves as a critical safeguard in the hiring process. Here's why it matters:

**1. Protecting Your Organization's Reputation**
In today's connected world, news travels fast. A single incident involving an employee with a problematic background can severely damage your brand reputation. Background checks help identify potential risks before they become problems.

**2. Ensuring Workplace Safety**
Every employee has the right to work in a safe environment. By verifying criminal backgrounds and conducting reference checks, organizations can significantly reduce the risk of workplace incidents.

**3. Reducing Turnover Costs**
Thorough verification helps ensure candidates have the qualifications and experience they claim. This reduces the likelihood of performance issues and subsequent turnover.

**4. Compliance Requirements**
Many industries have regulatory requirements for employee screening. Proper background verification helps organizations maintain compliance and avoid legal issues.

**5. Building a Culture of Trust**
When employees know that everyone has gone through the same thorough screening process, it builds trust within the organization and promotes a culture of integrity.

At Aarambh India Service, we understand these challenges and provide comprehensive verification solutions tailored to your organization's needs. Our pan-India network ensures accurate, timely results that help you make confident hiring decisions.`,
    category: 'Hiring',
    author: 'Aarambh Team',
    date: 'December 15, 2025',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
  },
  {
    id: 2,
    title: 'Common Hiring Risks and How to Avoid Them',
    excerpt: 'From resume fraud to undisclosed criminal records, discover the most common hiring risks that organizations face and practical strategies to mitigate them through comprehensive verification.',
    content: `The hiring process is fraught with potential risks that can have lasting consequences for organizations. Understanding these risks is the first step toward building effective mitigation strategies.

**Common Hiring Risks:**

**1. Resume Fraud**
Studies suggest that up to 40% of resumes contain some form of falsification. This can range from exaggerated job titles to completely fabricated employment history. Our employment verification services directly contact previous employers to confirm tenure, designation, and performance.

**2. Undisclosed Criminal History**
Without proper screening, organizations may unknowingly hire individuals with criminal backgrounds that could pose risks to workplace safety or company assets. Our comprehensive criminal checks cover district courts, high courts, and central databases.

**3. Fake Educational Credentials**
The prevalence of fake degrees and certificates has increased significantly. We verify educational qualifications directly with institutions to ensure authenticity.

**4. Identity Fraud**
Candidates may use false identities to hide negative employment history or criminal records. Our identity verification includes PAN, Aadhaar, and other government ID validation.

**5. Reference Manipulation**
Some candidates provide fake references or coach their references to provide misleading information. Our structured reference check process helps identify inconsistencies.

**Mitigation Strategies:**

- Implement consistent verification processes for all hires
- Use multi-source verification to cross-check information
- Partner with experienced verification providers
- Establish clear hiring policies that include mandatory background checks
- Train HR teams on red flags and verification best practices

Aarambh India Service provides end-to-end verification solutions that address all these risks systematically.`,
    category: 'Risk Management',
    author: 'Aarambh Team',
    date: 'December 10, 2025',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80',
  },
  {
    id: 3,
    title: 'Compliance Challenges in Employee Verification',
    excerpt: 'Navigate the complex landscape of compliance requirements in background verification. Understand data protection laws and industry-specific regulations affecting verification processes.',
    content: `The landscape of compliance in employee verification is becoming increasingly complex. Organizations must balance thorough verification with legal requirements and candidate privacy rights.

**Key Compliance Considerations:**

**1. Data Protection Laws**
With the introduction of data protection regulations, organizations must ensure that personal data collected during verification is handled appropriately. This includes:
- Obtaining proper consent before conducting checks
- Limiting data collection to what's necessary
- Ensuring secure storage and transmission
- Providing candidates with access to their data

**2. Industry-Specific Regulations**
Different industries have varying requirements:
- **Banking & Finance**: RBI guidelines mandate thorough employee screening
- **Healthcare**: Medical professionals require license and credential verification
- **Education**: Background checks for those working with minors
- **IT Services**: Client-driven verification requirements

**3. Fair Employment Practices**
Verification processes must not discriminate based on protected characteristics. Organizations should:
- Apply consistent verification standards across all candidates
- Consider the relevance of findings to the specific role
- Provide candidates an opportunity to explain adverse findings

**4. Documentation Requirements**
Maintaining proper documentation is crucial for compliance:
- Consent forms
- Verification reports
- Decision-making records
- Data retention policies

**Best Practices for Compliance:**

1. Develop clear, written verification policies
2. Train all stakeholders on compliance requirements
3. Partner with verified, ISO-certified providers
4. Regular audit of verification processes
5. Stay updated on regulatory changes

Aarambh India Service maintains ISO 9001:2015 certification and follows strict compliance protocols to help organizations meet their regulatory obligations.`,
    category: 'Compliance',
    author: 'Aarambh Team',
    date: 'December 5, 2025',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?w=800&q=80',
  },
  {
    id: 4,
    title: 'Best Practices for Faster Hiring with Accurate Verification',
    excerpt: 'Speed and accuracy don\'t have to be trade-offs. Learn how modern verification technologies and streamlined processes can help you hire faster without compromising on thoroughness.',
    content: `In today's competitive talent market, speed is essential. However, rushing through verification can lead to costly mistakes. Here's how to achieve both speed and accuracy.

**Strategies for Faster Verification:**

**1. Digital-First Approach**
Leverage technology to accelerate verification:
- API integration with HRMS systems
- Digital document submission
- Automated data extraction
- Real-time status tracking

**2. Parallel Processing**
Don't wait for one check to complete before starting another:
- Initiate all applicable checks simultaneously
- Use a coordinated approach to manage multiple verifications
- Set up automated triggers and notifications

**3. Prioritized Verification**
Not all checks take the same time:
- Start with checks that have longer TATs
- Complete quick digital verifications first for preliminary clearance
- Use conditional offers where appropriate

**4. Pre-verified Talent Pools**
Build relationships with candidates before you need them:
- Maintain verified candidate databases
- Periodic re-verification of active candidates
- Partner with verified staffing agencies

**5. Clear Documentation Requirements**
Delays often occur due to incomplete submissions:
- Provide clear checklists to candidates
- Use digital forms with validation
- Offer multiple submission channels

**Maintaining Accuracy:**

- Never compromise on verification sources
- Cross-reference information from multiple sources
- Use experienced verification professionals
- Implement quality control checkpoints
- Regular audits and process improvements

**Technology Solutions:**

- Workflow automation for consistent processes
- Machine learning for document verification
- Integration APIs for seamless data flow
- Real-time dashboards for visibility

At Aarambh, we've optimized our processes to deliver both speed and accuracy, with defined TATs and 99.9% accuracy rates.`,
    category: 'Best Practices',
    author: 'Aarambh Team',
    date: 'November 28, 2025',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80',
  },
  {
    id: 5,
    title: 'Insurance Claim Verification: Reducing Fraud & Risk',
    excerpt: 'Insurance fraud costs the industry billions annually. Discover how comprehensive claim verification processes help insurers identify fraudulent claims and protect their bottom line.',
    content: `Insurance fraud is a significant challenge that costs the industry billions of rupees annually. Effective claim verification is essential for identifying and preventing fraudulent claims.

**Types of Insurance Fraud:**

**1. Application Fraud**
- False information on policy applications
- Non-disclosure of pre-existing conditions
- Identity misrepresentation

**2. Claim Fraud**
- Staged accidents
- Exaggerated claims
- False documentation
- Multiple claims for same incident

**3. Internal Fraud**
- Collusion with policyholders
- Processing fake claims
- Manipulation of records

**Verification Approaches:**

**1. Policyholder Verification**
Before issuing policies:
- Identity verification
- Address confirmation
- Medical history validation
- Previous claim history check

**2. Claim Investigation**
When claims are filed:
- Incident verification
- Document authentication
- Witness statements
- Field investigation

**3. Continuous Monitoring**
Ongoing surveillance:
- Pattern analysis
- Cross-reference with databases
- Social media monitoring
- Industry fraud databases

**Red Flags to Watch:**

- Inconsistent documentation
- Delayed reporting
- Multiple similar claims
- Reluctance to provide information
- Pressure for quick settlement

**Technology in Fraud Detection:**

- Data analytics for pattern recognition
- Image forensics for document verification
- Geolocation verification
- Database cross-referencing

**Benefits of Robust Verification:**

1. Reduced claim payouts for fraudulent claims
2. Lower premium rates for honest policyholders
3. Improved operational efficiency
4. Enhanced regulatory compliance
5. Better risk assessment

Aarambh India Service provides comprehensive insurance verification solutions with pan-India field investigation capabilities.`,
    category: 'Insurance',
    author: 'Aarambh Team',
    date: 'November 20, 2025',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80',
  },
  {
    id: 6,
    title: 'How Enterprises Can Build Trust Through Verification',
    excerpt: 'Trust is the foundation of successful business relationships. Learn how implementing transparent verification processes can enhance stakeholder confidence and organizational reputation.',
    content: `In an era of increasing scrutiny and accountability, trust has become a critical competitive advantage. Organizations that demonstrate commitment to integrity through robust verification processes build stronger relationships with all stakeholders.

**Building Trust with Employees:**

- Fair and consistent hiring practices
- Transparent verification policies
- Privacy-respecting processes
- Equal treatment regardless of position

**Building Trust with Clients:**

- Verified workforce handling client projects
- Compliance with contractual requirements
- Regular audit reports
- Quick response to verification requests

**Building Trust with Partners:**

- Vendor and supplier verification
- Third-party due diligence
- Continuous compliance monitoring
- Transparent business practices

**Key Elements of Trust-Building Verification:**

**1. Transparency**
- Clear communication about verification processes
- Candidate-friendly procedures
- Accessible policies and procedures

**2. Consistency**
- Same standards for all levels
- Documented processes
- Regular audits and updates

**3. Confidentiality**
- Secure data handling
- Limited access controls
- Proper disposal of sensitive information

**4. Compliance**
- Adherence to legal requirements
- Industry best practices
- Regular compliance reviews

**Measuring Trust:**

- Employee satisfaction surveys
- Client feedback and retention
- Partner relationship quality
- Regulatory compliance scores

**Case for Investment:**

Organizations with strong verification processes report:
- 25% lower employee turnover
- 40% reduction in workplace incidents
- Higher client retention rates
- Improved regulatory standing

Aarambh India Service helps enterprises build and maintain trust through our comprehensive, compliant, and confidential verification services.`,
    category: 'Enterprise',
    author: 'Aarambh Team',
    date: 'November 15, 2025',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80',
  },
  {
    id: 7,
    title: 'Field Verification vs Digital Verification: What Works Best',
    excerpt: 'Compare the strengths and limitations of field-based and digital verification methods. Understand when to use each approach and how hybrid models deliver the best results.',
    content: `The verification industry has evolved significantly with digital transformation. However, the debate between field-based and digital verification continues. Understanding when to use each approach is key to effective verification.

**Digital Verification:**

**Advantages:**
- Instant or near-instant results
- Lower cost per verification
- Consistent and standardized
- Easy to scale
- Comprehensive audit trails

**Best For:**
- Identity verification (PAN, Aadhaar, DL)
- Database checks (criminal, credit)
- Education verification (where digitized)
- Employment verification (with digital records)

**Limitations:**
- Dependent on database availability
- Cannot verify physical aspects
- May miss recent or unrecorded information
- Limited for rural or informal sector

**Field Verification:**

**Advantages:**
- Physical confirmation of information
- Can gather contextual insights
- Effective for addresses and references
- Captures non-digital information
- Human judgment on red flags

**Best For:**
- Address verification
- Reference checks
- Physical document verification
- Rural area verification
- Complex investigations

**Limitations:**
- Longer turnaround time
- Higher cost
- Variable quality based on investigator
- Geographic limitations

**The Hybrid Model:**

The most effective approach combines both:

1. **Start Digital**: Quick initial screening through database checks
2. **Risk-Based Field**: Deploy field verification for high-risk or unverifiable elements
3. **Cross-Reference**: Validate digital findings with field observations
4. **Continuous Updates**: Regular re-verification using both methods

**Choosing the Right Approach:**

Consider:
- Type of verification needed
- Candidate location
- Available databases
- Time constraints
- Budget considerations
- Risk tolerance

At Aarambh India Service, we employ a hybrid model with extensive digital capabilities and a pan-India field network to deliver accurate, timely verifications.`,
    category: 'Technology',
    author: 'Aarambh Team',
    date: 'November 8, 2025',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80',
  },
  {
    id: 8,
    title: 'The Future of Background Verification: AI and Automation',
    excerpt: 'Artificial intelligence is transforming the verification industry. Explore how AI-powered tools are improving accuracy, reducing turnaround times, and enhancing the verification experience.',
    content: `The background verification industry is undergoing a significant transformation driven by artificial intelligence and automation. These technologies are reshaping how verifications are conducted and delivered.

**Current AI Applications:**

**1. Document Processing**
- OCR for data extraction
- Document authenticity verification
- Automated data validation
- Error detection and correction

**2. Database Integration**
- Automated database queries
- Pattern matching algorithms
- Cross-reference verification
- Real-time status updates

**3. Risk Assessment**
- Predictive risk scoring
- Anomaly detection
- Pattern recognition
- Fraud probability assessment

**4. Process Automation**
- Workflow orchestration
- Automated communications
- Report generation
- Quality control checks

**Emerging Technologies:**

**1. Machine Learning**
- Improving accuracy over time
- Learning from verification outcomes
- Predicting verification timelines
- Identifying new risk patterns

**2. Natural Language Processing**
- Analyzing reference feedback
- Processing unstructured data
- Multilingual capabilities
- Sentiment analysis

**3. Computer Vision**
- Face matching
- Document forensics
- Signature verification
- Photo manipulation detection

**Impact on the Industry:**

**For Organizations:**
- Faster turnaround times
- Lower costs per verification
- More consistent quality
- Better risk insights

**For Candidates:**
- Smoother experience
- Faster onboarding
- More privacy-respecting
- Clearer communication

**Challenges:**

- Data privacy concerns
- Algorithm bias
- Technology integration
- Human oversight requirements

**The Human Element:**

Despite automation, human expertise remains crucial:
- Complex judgment calls
- Contextual assessment
- Candidate communication
- Quality assurance

**Future Outlook:**

- Increased real-time verification
- Blockchain for credential verification
- Biometric integration
- Continuous background monitoring

Aarambh India Service is investing in AI and automation while maintaining the human expertise that ensures accurate, fair, and comprehensive verifications.`,
    category: 'Technology',
    author: 'Aarambh Team',
    date: 'November 1, 2025',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&q=80',
  },
];

// Categories
const categories = [
  'All',
  'Hiring',
  'Risk Management',
  'Compliance',
  'Best Practices',
  'Insurance',
  'Enterprise',
  'Technology',
];

export default function Blog() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedPost, setSelectedPost] = useState(null);

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const handleReadMore = (post) => {
    setSelectedPost(post);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClosePost = () => {
    setSelectedPost(null);
  };

  // If a post is selected, show the full article
  if (selectedPost) {
    return (
      <div className="overflow-hidden">
        {/* Article Header */}
        <section className="relative bg-slate-900 text-white py-20" data-testid="article-header">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <button 
              onClick={handleClosePost}
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
              data-testid="back-to-blog-btn"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Blog
            </button>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-sm font-medium">
                {selectedPost.category}
              </span>
              <span className="text-slate-400 text-sm flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {selectedPost.readTime}
              </span>
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold mb-6">
              {selectedPost.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {selectedPost.author}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {selectedPost.date}
              </span>
            </div>
          </div>
        </section>

        {/* Article Image */}
        <section className="relative -mt-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <img 
              src={selectedPost.image} 
              alt={selectedPost.title}
              className="w-full h-64 md:h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="py-16 bg-white" data-testid="article-content">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              {selectedPost.content.split('\n\n').map((paragraph, index) => {
                if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                  return (
                    <h2 key={index} className="text-2xl font-bold text-slate-900 mt-8 mb-4">
                      {paragraph.replace(/\*\*/g, '')}
                    </h2>
                  );
                } else if (paragraph.startsWith('**')) {
                  return (
                    <h3 key={index} className="text-xl font-semibold text-slate-900 mt-6 mb-3">
                      {paragraph.replace(/\*\*/g, '')}
                    </h3>
                  );
                } else if (paragraph.startsWith('- ')) {
                  return (
                    <ul key={index} className="list-disc pl-6 my-4 space-y-2">
                      {paragraph.split('\n').map((item, i) => (
                        <li key={i} className="text-slate-600">
                          {item.replace('- ', '')}
                        </li>
                      ))}
                    </ul>
                  );
                } else {
                  return (
                    <p key={index} className="text-slate-600 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  );
                }
              })}
            </div>

            {/* Author & Share */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold">
                    A
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{selectedPost.author}</p>
                    <p className="text-sm text-slate-500">Published on {selectedPost.date}</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleClosePost}
                  data-testid="view-more-articles-btn"
                >
                  View More Articles
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        <section className="py-16 bg-slate-50" data-testid="related-posts">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {blogPosts
                .filter(post => post.id !== selectedPost.id && post.category === selectedPost.category)
                .slice(0, 3)
                .map((post) => (
                  <Card key={post.id} className="overflow-hidden border-slate-100 card-hover cursor-pointer" onClick={() => handleReadMore(post)}>
                    <div className="aspect-video">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-slate-600 text-sm line-clamp-2">
                        {post.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white py-20 lg:py-28" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <TrendingUp className="w-4 h-4" />
              Insights & Resources
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6">
              Blog & Knowledge Center
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Educational content and thought-leadership articles on hiring, risk management, 
              compliance, insurance verification, and fraud prevention.
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b border-slate-100" data-testid="blog-categories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((category, index) => (
              <button
                key={index}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`category-${category.toLowerCase().replace(' ', '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16 bg-white" data-testid="featured-post">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="overflow-hidden border-slate-100 shadow-soft cursor-pointer card-hover" onClick={() => handleReadMore(filteredPosts[0])}>
            <div className="grid md:grid-cols-2">
              <div className="aspect-video md:aspect-auto">
                <img 
                  src={filteredPosts[0]?.image} 
                  alt={filteredPosts[0]?.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-4 mb-4">
                  <span className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
                    Featured
                  </span>
                  <span className="text-slate-500 text-sm flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {filteredPosts[0]?.category}
                  </span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
                  {filteredPosts[0]?.title}
                </h2>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {filteredPosts[0]?.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {filteredPosts[0]?.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {filteredPosts[0]?.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {filteredPosts[0]?.readTime}
                    </span>
                  </div>
                </div>
              </CardContent>
            </div>
          </Card>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 bg-slate-50" data-testid="blog-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Latest Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.slice(1).map((post) => (
              <Card key={post.id} className="overflow-hidden border-slate-100 card-hover">
                <div className="aspect-video cursor-pointer" onClick={() => handleReadMore(post)}>
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs font-medium">
                      {post.category}
                    </span>
                    <span className="text-slate-400 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 
                    className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2 cursor-pointer hover:text-emerald-600 transition-colors"
                    onClick={() => handleReadMore(post)}
                  >
                    {post.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {post.date}
                    </span>
                    <button 
                      onClick={() => handleReadMore(post)}
                      className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700 transition-colors"
                      data-testid={`read-more-${post.id}`}
                    >
                      Read More
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Stay Updated
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Get the latest insights on background verification, compliance updates, 
            and industry best practices delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg border border-slate-200 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
              data-testid="newsletter-email-input"
            />
            <Button 
              className="bg-slate-900 hover:bg-slate-800 text-white px-6"
              data-testid="newsletter-subscribe-btn"
            >
              Subscribe
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-slate-900" data-testid="blog-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Have Questions About Verification?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Our team is ready to help you understand how background verification 
            can benefit your organization.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-6 text-base"
              data-testid="blog-contact-btn"
            >
              Contact Us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
